import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const stats = [
  {
    value: 60,
    suffix: '%+',
    label: 'Of corporate emissions sit in supply chains and SMEs',
    description: 'Without supplier readiness, Scope 3 targets and net-zero commitments fail.'
  },
  {
    value: 85,
    suffix: '%',
    label: 'Of asset managers integrate sustainability into investment decisions',
    description: 'ESG performance now directly influences capital allocation and cost of capital.'
  },
  {
    value: 3,
    prefix: 'US$',
    suffix: 'T',
    label: 'Annual global climate investment gap',
    description: 'Projects fail not due to lack of capital, but lack of investment readiness.'
  },
  {
    value: 30,
    prefix: '<',
    suffix: '%',
    label: 'Of carbon projects successfully reach credit issuance',
    description: 'Weak feasibility assessment, poor project design, and MRV gaps stall delivery.'
  },
  {
    value: 25,
    prefix: '<',
    suffix: '%',
    label: 'Of companies have mature ESG governance and data systems',
    description: 'Most strategies remain disclosure-driven, not decision-ready.'
  }
];

const Counter = ({ value, prefix = '', suffix = '', isInView }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <span>
      {prefix}{count}{suffix}
    </span>
  );
};

export const Stats = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      data-testid="stats-section"
      className="py-16 md:py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-14"
        >
          <span className="font-sans text-sm font-semibold text-brand-brown uppercase tracking-wider">
            The Reality
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mt-4 mb-6">
            The Sustainability Readiness Gap is Real
          </h2>
          <p className="font-sans text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            The numbers speak for themselves. The window for action is closing, 
            but the opportunity for leadership is now.
          </p>
        </motion.div>

        {/* Stats - Modern minimal layout */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative py-8 px-6 text-center group ${
                index !== stats.length - 1 ? 'md:border-r border-gray-200' : ''
              }`}
              data-testid={`stat-card-${index}`}
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-brand-green to-brand-brown rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Number */}
              <div className="mb-4">
                <span className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-brown to-brand-green">
                  <Counter 
                    value={stat.value} 
                    prefix={stat.prefix || ''} 
                    suffix={stat.suffix || ''} 
                    isInView={isInView}
                  />
                </span>
              </div>
              
              {/* Label */}
              <h3 className="font-sans text-sm font-medium text-brand-dark mb-3 leading-relaxed min-h-[60px]">
                {stat.label}
              </h3>
              
              {/* Divider dot */}
              <div className="w-1.5 h-1.5 bg-brand-green rounded-full mx-auto mb-3"></div>
              
              {/* Description */}
              <p className="font-sans text-xs text-gray-500 leading-relaxed italic">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
